'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReviewPriority } from '@/lib/ai-review-priority';
import type { PrV2CommentTarget } from '@/lib/pr-v2/tabs';
import type { PrV2CommentResponse, PrV2ReviewThread } from '@/lib/pr-v2/types';
import { useCallback, useMemo } from 'react';
import { getPrV2CommentCommitSha } from '../diff/lib/pr-v2-comment-version-navigation';
import { getSourceLabel } from './bot-display-labels';
import { normalizePrV2CommentBody } from './pr-v2-comment-content';
import { getMappedThreadFirstComment, getMappedThreadFirstGithubCommentId, isBotOrAgentLogin, isDisplayedCubicLogin } from './pr-v2-cubic-review-utils';
import { usePrV2LocalCommentModel } from '@/data/v2/pr/local-comment-model';
import { usePrV2Comments } from '@/data/v2/pr/use-pr-v2-comments';
import { usePrV2CubicReviewSummary } from '@/data/v2/pr/use-pr-v2-cubic-review-summary';
import { usePrV2ReviewThreads } from '@/data/v2/pr/use-pr-v2-review-threads';
import { severityToPriority } from '@/lib/ai-review-priority';
import { removeInternalFindingSourceNote } from '@/lib/ai/code-review-service/utils/internal-finding-source-note';
import { removeMetadataFromComment, removePriorityLabel } from '@/lib/ai/code-review-service/utils/metadata';
import { PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
export type PrV2CommentThreadRow = {
  threadId: string;
  githubCommentId: string;
  userLogin: string | null;
  userAvatarUrl: string | null;
  // Friendly display name for the comment source (e.g. "cubic", "Copilot", or
  // a human's login). Rendered in the row's meta line when the row shows no priority.
  sourceLabel: string | null;
  // cubic-only severity score (0-10) used to render the row's priority.
  // null for non-cubic bots and for humans, whose rows show an avatar instead.
  severityScore: number | null;
  path: string | null;
  line: number | null;
  preview: string;
  copyText: string;
  replyCount: number;
  status: 'open' | 'resolved' | 'outdated';
  /**
   * When GitHub created the thread's first comment; null when it didn't say. The list formats
   * its age as it renders, so the age moves on while the panel stays open.
   */
  createdAt: string | null;
  /** When the thread was opened, in milliseconds since the epoch; 0 when unknown. */
  openedAt: number;
};
export function getThreadLocation(row: PrV2CommentThreadRow): string | null {
  const fileName = row.path?.split('/').pop();
  if (!fileName) {
    return row.line === null ? null : `line ${row.line}`;
  }
  return row.line === null ? fileName : `${fileName}:${row.line}`;
}
export function getThreadPriority(row: PrV2CommentThreadRow): ReviewPriority | null {
  return row.severityScore === null ? null : severityToPriority(row.severityScore);
}
const EMPTY_COMMENTS: PrV2CommentResponse[] = [];

/**
 * The key that connects a comment to the review thread nodes it belongs to. Threads use GraphQL
 * node ids, while some local or optimistic comments only have REST comment ids, so those fall back
 * to a key of their own.
 */
function getCommentLookupId(comment: PrV2CommentResponse): string {
  const nodeId = asRecord(comment.raw)?.node_id;
  return typeof nodeId === 'string' ? nodeId : `comment:${comment.githubCommentId}`;
}
function getThreadLine(thread: {
  line?: number | null;
  originalLine?: number | null;
  comments: {
    nodes: Array<{
      line?: number | null;
      originalLine?: number | null;
    }>;
  };
}): number | null {
  const line = thread.line ?? thread.originalLine ?? thread.comments.nodes[0]?.line ?? thread.comments.nodes[0]?.originalLine ?? null;
  return typeof line === 'number' ? line : null;
}
function getThreadStatus(thread: {
  isResolved: boolean;
  isOutdated: boolean;
}): PrV2CommentThreadRow['status'] {
  if (thread.isOutdated) {
    return 'outdated';
  }
  if (thread.isResolved) {
    return 'resolved';
  }
  return 'open';
}
function getThreadPreview(copyText: string, showsPriority: boolean): string {
  if (copyText === '(no content)') {
    return copyText;
  }
  const preview = removeMetadataFromComment(copyText)
  // Drop HTML comments that can leak through copied violation bodies.
  .replace(/<!--[\s\S]*?-->/g, ' ')
  // Strip simple markdown affordances for compact row previews.
  .replace(/^\s{0,3}#{1,6}\s+/gm, '').replace(/\*\*([^*]+)\*\*/g, '$1').replace(/__([^_]+)__/g, '$1').replace(/`([^`]+)`/g, '$1').replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\s+/g, ' ').trim();

  // The row shows the priority itself, so its preview drops cubic's leading "P1:" label.
  return (showsPriority ? removePriorityLabel(preview) : preview) || '(no content)';
}
function getThreadCopyText(comment: {
  body?: string | null;
  userLogin?: string | null;
  inReplyToId?: string | null;
} | null): string {
  if (!comment) {
    return '(no content)';
  }
  const normalized = normalizePrV2CommentBody({
    // cubic's sanitizer drops the internal note's HTML markers, so the note has to come off first.
    body: removeInternalFindingSourceNote(comment.body ?? ''),
    userLogin: comment.userLogin ?? null,
    inReplyToId: comment.inReplyToId ?? null
  });
  return normalized.replace(/\s+/g, ' ').trim() || '(no content)';
}
function getThreadOpenedAt(comment: {
  createdAtGithub?: string | null;
  updatedAtGithub?: string | null;
} | null): number {
  // Rows show when the thread was opened, so they sort by it too; an edit to the comment would
  // otherwise move an old thread above newer ones.
  const timestamp = new Date(comment?.createdAtGithub ?? comment?.updatedAtGithub ?? 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}
function buildThreadRow(thread: PrV2ReviewThread, commentsByNodeId: Map<string, PrV2CommentResponse>, severityByCommentId: Record<string, number> | undefined): PrV2CommentThreadRow | null {
  const firstComment = getMappedThreadFirstComment(thread, commentsByNodeId);
  const githubCommentId = getMappedThreadFirstGithubCommentId(thread, commentsByNodeId);
  if (!firstComment || !githubCommentId) {
    return null;
  }
  const userLogin = firstComment.userLogin ?? null;
  const severityScore = severityByCommentId?.[githubCommentId] ?? null;
  const copyText = getThreadCopyText(firstComment);
  return {
    threadId: thread.id,
    githubCommentId,
    userLogin,
    userAvatarUrl: firstComment.userAvatarUrl,
    sourceLabel: getSourceLabel(userLogin),
    severityScore,
    path: thread.path ?? firstComment.path ?? null,
    line: getThreadLine(thread),
    preview: getThreadPreview(copyText, severityScore !== null),
    copyText,
    replyCount: Math.max(0, thread.comments.nodes.length - 1),
    status: getThreadStatus(thread),
    createdAt: firstComment.createdAtGithub ?? null,
    openedAt: getThreadOpenedAt(firstComment)
  };
}

/**
 * Open threads first, then cubic's before other bots', by severity, newest first. Human threads
 * are never cubic's and carry no severity, so they come out open first, newest first.
 */
function compareThreadRows(a: PrV2CommentThreadRow, b: PrV2CommentThreadRow): number {
  const closedOrder = Number(a.status !== 'open') - Number(b.status !== 'open');
  if (closedOrder !== 0) {
    return closedOrder;
  }
  const cubicOrder = Number(!isDisplayedCubicLogin(a.userLogin)) - Number(!isDisplayedCubicLogin(b.userLogin));
  if (cubicOrder !== 0) {
    return cubicOrder;
  }

  // Higher severity first (descending)
  const severityOrder = (b.severityScore ?? -1) - (a.severityScore ?? -1);
  if (severityOrder !== 0) {
    return severityOrder;
  }
  return b.openedAt - a.openedAt;
}

/**
 * The open comments, bots' and humans' apart. Bots' are the open bot threads as the popover lists
 * them, plus the summary's outstanding cubic comments that have no thread here (still loading,
 * say), which still count as open.
 */
function countOpenComments(botRows: PrV2CommentThreadRow[], humanRows: PrV2CommentThreadRow[], summaryCommentIds: string[]): {
  bot: number;
  human: number;
} {
  const listedBotCommentIds = new Set(botRows.map(row => row.githubCommentId));
  const unlistedSummaryCount = summaryCommentIds.filter(commentId => !listedBotCommentIds.has(commentId)).length;
  return {
    bot: botRows.filter(row => row.status === 'open').length + unlistedSummaryCount,
    human: humanRows.filter(row => row.status === 'open').length
  };
}

/**
 * The PR's review threads as the review tab bar's comment controls and the comments popover show
 * them: split into bots and humans, sorted, and counted.
 */
export function usePrV2CommentThreads(t0) {
  const $ = _c(42);
  const {
    owner,
    repo,
    number
  } = t0;
  const commentsQuery = usePrV2Comments(owner, repo, number);
  const comments = commentsQuery.data ?? EMPTY_COMMENTS;
  let t1;
  if ($[0] !== comments) {
    t1 = comments.some(_temp);
    $[0] = comments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasReviewComments = t1;
  let t2;
  if ($[2] !== hasReviewComments) {
    t2 = {
      enabled: hasReviewComments
    };
    $[2] = hasReviewComments;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const reviewThreadsQuery = usePrV2ReviewThreads(owner, repo, number, t2);
  let t3;
  if ($[4] !== comments || $[5] !== number || $[6] !== owner || $[7] !== repo || $[8] !== reviewThreadsQuery.data) {
    t3 = {
      owner,
      repo,
      number,
      comments,
      reviewThreads: reviewThreadsQuery.data
    };
    $[4] = comments;
    $[5] = number;
    $[6] = owner;
    $[7] = repo;
    $[8] = reviewThreadsQuery.data;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    reviewThreads,
    reviewThreadByCommentNodeId
  } = usePrV2LocalCommentModel(t3);
  let t4;
  if ($[10] !== comments) {
    t4 = new Map(comments.map(_temp2));
    $[10] = comments;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const commentsByNodeId = t4;
  let t5;
  if ($[12] !== comments) {
    t5 = new Map(comments.map(_temp3));
    $[12] = comments;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const commentsByGithubCommentId = t5;
  const {
    data: cubicReviewSummary
  } = usePrV2CubicReviewSummary(owner, repo, number);
  const severityByCommentId = cubicReviewSummary?.severityByCommentId;
  let t6;
  if ($[14] !== commentsByNodeId || $[15] !== reviewThreads || $[16] !== severityByCommentId) {
    let t7;
    if ($[18] !== commentsByNodeId || $[19] !== severityByCommentId) {
      t7 = thread => buildThreadRow(thread, commentsByNodeId, severityByCommentId) ?? [];
      $[18] = commentsByNodeId;
      $[19] = severityByCommentId;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    t6 = reviewThreads.flatMap(t7).sort(compareThreadRows);
    $[14] = commentsByNodeId;
    $[15] = reviewThreads;
    $[16] = severityByCommentId;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const threadRows = t6;
  let t7;
  if ($[21] !== threadRows) {
    t7 = threadRows.filter(_temp4);
    $[21] = threadRows;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const botThreadRows = t7;
  let t8;
  if ($[23] !== threadRows) {
    t8 = threadRows.filter(_temp5);
    $[23] = threadRows;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const humanThreadRows = t8;
  const summaryOutstandingCommentIds = cubicReviewSummary?.outstandingCommentIds;
  let t9;
  if ($[25] !== summaryOutstandingCommentIds) {
    t9 = summaryOutstandingCommentIds ?? [];
    $[25] = summaryOutstandingCommentIds;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== botThreadRows || $[28] !== humanThreadRows || $[29] !== t9) {
    t10 = countOpenComments(botThreadRows, humanThreadRows, t9);
    $[27] = botThreadRows;
    $[28] = humanThreadRows;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const openCounts = t10;
  let t11;
  if ($[31] !== commentsByGithubCommentId || $[32] !== reviewThreadByCommentNodeId) {
    t11 = githubCommentId => {
      const comment_2 = commentsByGithubCommentId.get(githubCommentId);
      const thread_0 = comment_2 ? reviewThreadByCommentNodeId.get(getCommentLookupId(comment_2)) : undefined;
      const targetHeadSha = getPrV2CommentCommitSha(comment_2);
      return {
        tab: PR_V2_TABS.CHANGES,
        commentId: githubCommentId,
        filename: comment_2?.path ?? thread_0?.path ?? undefined,
        ...(targetHeadSha ? {
          targetHeadSha
        } : {})
      };
    };
    $[31] = commentsByGithubCommentId;
    $[32] = reviewThreadByCommentNodeId;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const getCommentTarget = t11;
  const t12 = commentsQuery.isLoading || reviewThreadsQuery.isLoading;
  let t13;
  if ($[34] !== botThreadRows || $[35] !== cubicReviewSummary || $[36] !== getCommentTarget || $[37] !== humanThreadRows || $[38] !== openCounts.bot || $[39] !== openCounts.human || $[40] !== t12) {
    t13 = {
      isLoading: t12,
      cubicReviewSummary,
      botThreadRows,
      humanThreadRows,
      openBotCount: openCounts.bot,
      openHumanCount: openCounts.human,
      getCommentTarget
    };
    $[34] = botThreadRows;
    $[35] = cubicReviewSummary;
    $[36] = getCommentTarget;
    $[37] = humanThreadRows;
    $[38] = openCounts.bot;
    $[39] = openCounts.human;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
}
function _temp5(row_0) {
  return row_0.userLogin !== null && !isBotOrAgentLogin(row_0.userLogin);
}
function _temp4(row) {
  return isBotOrAgentLogin(row.userLogin);
}
function _temp3(comment_1) {
  return [comment_1.githubCommentId, comment_1] as const;
}
function _temp2(comment_0) {
  return [getCommentLookupId(comment_0), comment_0] as const;
}
function _temp(comment) {
  return comment.commentType === "review_comment";
}
export type PrV2CommentThreads = ReturnType<typeof usePrV2CommentThreads>;